'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { useTheme } from '@/contexts/ThemeContext'
import { getSupabaseClient } from '@/utils/supabase/singleton'
import { useAuthStore } from '@/store/useAuthStore'
import { ArrowLeft, Upload, X, ImageIcon } from 'lucide-react'
import Image from 'next/image'
import { parseAuthToken } from '@/utils/base64'
import logger from '@/utils/logger'

type MarketCategory = {
  id: string
  name: string
  description: string
  icon: string
}

type ImageFile = {
  file: File
  preview: string
  isPrimary: boolean
}

export default function NewMarketItemPage() {
  const router = useRouter()
  const { theme } = useTheme()
  const { user } = useAuthStore()
  const [profile, setProfile] = useState<any>(null)
  const [loading, setLoading] = useState(false)

  const [categories, setCategories] = useState<MarketCategory[]>([])
  const [images, setImages] = useState<ImageFile[]>([])

  // Form fields
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [price, setPrice] = useState('')
  const [brand, setBrand] = useState('')
  const [model, setModel] = useState('')
  const [purchaseDate, setPurchaseDate] = useState('')
  const [usageCount, setUsageCount] = useState('')
  const [size, setSize] = useState('')
  const [maintenanceDate, setMaintenanceDate] = useState('')
  const [condition, setCondition] = useState('상')
  const [transactionType, setTransactionType] = useState('both')
  const [region, setRegion] = useState('')

  useEffect(() => {
    loadProfile()
    loadCategories()
  }, [])

  const loadProfile = async () => {
    try {
      const token = localStorage.getItem('auth_token')
      if (!token) {
        router.push('/login')
        return
      }

      const decoded = parseAuthToken(token)
      if (!decoded) {
        router.push('/login')
        return
      }

      const supabase = getSupabaseClient()
      let query = supabase.from('user_profiles').select('*')

      if (decoded.kakao_id) {
        query = query.eq('kakao_id', decoded.kakao_id)
      } else if (decoded.naver_id) {
        query = query.eq('naver_id', decoded.naver_id)
      } else {
        router.push('/login')
        return
      }

      const { data } = await query.single()
      if (data) {
        setProfile(data)
      } else {
        router.push('/login')
      }
    } catch (error) {
      logger.error('Error loading profile:', error)
      router.push('/login')
    }
  }

  const loadCategories = async () => {
    try {
      const supabase = getSupabaseClient()
      const { data, error } = await supabase
        .from('market_categories')
        .select('*')
        .order('display_order', { ascending: true })

      if (error) throw error
      setCategories(data || [])
      if (data && data.length > 0) {
        setSelectedCategoryId(data[0].id)
      }
    } catch (error) {
      logger.error('Error loading categories:', error)
    }
  }

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files) return

    const newImages: ImageFile[] = []
    const maxImages = 10

    for (let i = 0; i < Math.min(files.length, maxImages - images.length); i++) {
      const file = files[i]
      if (!file.type.startsWith('image/')) continue

      newImages.push({
        file,
        preview: URL.createObjectURL(file),
        isPrimary: images.length === 0 && i === 0
      })
    }

    setImages([...images, ...newImages])
  }

  const removeImage = (index: number) => {
    const newImages = images.filter((_, i) => i !== index)

    // If removed image was primary, make first image primary
    if (images[index].isPrimary && newImages.length > 0) {
      newImages[0].isPrimary = true
    }

    setImages(newImages)
  }

  const setPrimaryImage = (index: number) => {
    const newImages = images.map((img, i) => ({
      ...img,
      isPrimary: i === index
    }))
    setImages(newImages)
  }

  const uploadImagesToStorage = async (itemId: string, userId: string) => {
    const supabase = getSupabaseClient()
    const uploadedUrls: Array<{ url: string; isPrimary: boolean; order: number }> = []

    for (let i = 0; i < images.length; i++) {
      const image = images[i]
      const fileExt = image.file.name.split('.').pop()
      const fileName = `${userId}/${itemId}/${Date.now()}_${i}.${fileExt}`

      console.log('Uploading image:', fileName)

      const { data, error } = await supabase.storage
        .from('market-images')
        .upload(fileName, image.file)

      if (error) {
        logger.error('Error uploading image:', error)
        console.error('Upload error details:', error)
        continue
      }

      console.log('Upload success:', data)

      const { data: { publicUrl } } = supabase.storage
        .from('market-images')
        .getPublicUrl(fileName)

      console.log('Public URL generated:', publicUrl)

      uploadedUrls.push({
        url: publicUrl,
        isPrimary: image.isPrimary,
        order: i
      })
    }

    console.log('All uploaded URLs:', uploadedUrls)
    return uploadedUrls
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!profile) {
      router.push('/login')
      return
    }

    if (!title || !description || !price || !selectedCategoryId) {
      alert('필수 항목을 모두 입력해주세요.')
      return
    }

    if (images.length === 0) {
      alert('상품 이미지를 최소 1장 이상 업로드해주세요.')
      return
    }

    setLoading(true)

    try {
      const supabase = getSupabaseClient()

      // Insert market item
      const { data: itemData, error: itemError } = await supabase
        .from('market_items')
        .insert({
          seller_id: profile.id,
          category_id: selectedCategoryId,
          title,
          description,
          price: parseInt(price),
          brand: brand || null,
          model: model || null,
          purchase_date: purchaseDate || null,
          usage_count: usageCount || null,
          size: size || null,
          maintenance_date: maintenanceDate || null,
          condition,
          transaction_type: transactionType,
          region: region || null,
          status: 'available'
        })
        .select()
        .single()

      if (itemError) throw itemError

      // Upload images
      const uploadedImages = await uploadImagesToStorage(itemData.id, profile.id)

      // Insert image records
      const imageRecords = uploadedImages.map(img => ({
        item_id: itemData.id,
        image_url: img.url,
        is_primary: img.isPrimary,
        display_order: img.order
      }))

      const { error: imagesError } = await supabase
        .from('market_images')
        .insert(imageRecords)

      if (imagesError) throw imagesError

      alert('상품이 등록되었습니다!')
      router.push(`/market/${itemData.id}`)
    } catch (error) {
      logger.error('Error creating item:', error)
      alert('상품 등록 중 오류가 발생했습니다.')
    } finally {
      setLoading(false)
    }
  }

  const regions = ['서울', '경기', '인천', '강원', '충북', '충남', '대전', '세종', '경북', '경남', '대구', '울산', '부산', '전북', '전남', '광주', '제주']
  const conditions = ['최상', '상', '중', '하']

  return (
    <div className={`min-h-screen ${theme === 'light' ? 'bg-gray-50' : 'bg-gray-900'}`}>
      {/* Header */}
      <div className={`sub-sticky z-20 backdrop-blur-xl border-b ${
        theme === 'light' ? 'bg-white/80 border-gray-200' : 'bg-gray-900/80 border-gray-800'
      }`}>
        <div className="max-w-3xl mx-auto px-3 py-3 flex items-center gap-2">
          <button
            onClick={() => router.back()}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className={`text-lg font-bold ${theme === 'light' ? 'text-gray-900' : 'text-white'}`}>
            상품 등록
          </h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="max-w-3xl mx-auto px-3 py-4">
        {/* Image Upload */}
        <div className="mb-6">
          <label className={`block text-sm font-semibold mb-2 ${
            theme === 'light' ? 'text-gray-700' : 'text-gray-300'
          }`}>
            상품 이미지 <span className="text-red-500">*</span>
            <span className="ml-2 text-xs font-normal text-gray-500">
              ({images.length}/10)
            </span>
          </label>

          <div className="grid grid-cols-4 gap-2">
            {/* Existing images */}
            {images.map((image, index) => (
              <div
                key={index}
                className={`relative aspect-square rounded-lg overflow-hidden border-2 ${
                  image.isPrimary
                    ? 'border-blue-500'
                    : 'border-gray-300 dark:border-gray-600'
                }`}
              >
                <Image
                  src={image.preview}
                  alt={`Preview ${index + 1}`}
                  fill
                  className="object-cover"
                />
                {image.isPrimary && (
                  <div className="absolute top-1 left-1 bg-blue-500 text-white text-xs px-2 py-0.5 rounded">
                    대표
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => removeImage(index)}
                  className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                >
                  <X className="w-3 h-3" />
                </button>
                {!image.isPrimary && (
                  <button
                    type="button"
                    onClick={() => setPrimaryImage(index)}
                    className="absolute bottom-1 left-1 right-1 bg-black/50 text-white text-xs py-1 rounded hover:bg-black/70"
                  >
                    대표로 설정
                  </button>
                )}
              </div>
            ))}

            {/* Upload button */}
            {images.length < 10 && (
              <label className={`aspect-square rounded-lg border-2 border-dashed cursor-pointer flex flex-col items-center justify-center ${
                theme === 'light'
                  ? 'border-gray-300 hover:border-blue-500 bg-gray-50 hover:bg-gray-100'
                  : 'border-gray-600 hover:border-blue-500 bg-gray-800 hover:bg-gray-700'
              }`}>
                <Upload className="w-8 h-8 text-gray-400 mb-2" />
                <span className="text-xs text-gray-500">이미지 추가</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </label>
            )}
          </div>
        </div>

        {/* Category */}
        <div className="mb-6">
          <label className={`block text-sm font-semibold mb-2 ${
            theme === 'light' ? 'text-gray-700' : 'text-gray-300'
          }`}>
            카테고리 <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-4 gap-2">
            {categories.map((category) => (
              <button
                key={category.id}
                type="button"
                onClick={() => setSelectedCategoryId(category.id)}
                className={`flex flex-col items-center gap-1 p-3 rounded-lg transition-all ${
                  selectedCategoryId === category.id
                    ? 'bg-blue-500 text-white'
                    : theme === 'light'
                      ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                <span className="text-2xl">{category.icon}</span>
                <span className="text-xs font-medium">{category.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Title */}
        <div className="mb-6">
          <label className={`block text-sm font-semibold mb-2 ${
            theme === 'light' ? 'text-gray-700' : 'text-gray-300'
          }`}>
            제목 <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="상품 제목을 입력하세요"
            maxLength={100}
            className={`w-full px-4 py-3 rounded-lg border ${
              theme === 'light'
                ? 'bg-white border-gray-300 text-gray-900'
                : 'bg-gray-800 border-gray-700 text-white'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            required
          />
        </div>

        {/* Description */}
        <div className="mb-6">
          <label className={`block text-sm font-semibold mb-2 ${
            theme === 'light' ? 'text-gray-700' : 'text-gray-300'
          }`}>
            상세 설명 <span className="text-red-500">*</span>
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="상품에 대한 자세한 설명을 입력하세요"
            rows={6}
            className={`w-full px-4 py-3 rounded-lg border ${
              theme === 'light'
                ? 'bg-white border-gray-300 text-gray-900'
                : 'bg-gray-800 border-gray-700 text-white'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            required
          />
        </div>

        {/* Price */}
        <div className="mb-6">
          <label className={`block text-sm font-semibold mb-2 ${
            theme === 'light' ? 'text-gray-700' : 'text-gray-300'
          }`}>
            가격 <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              type="text"
              value={price ? parseInt(price.replace(/,/g, '')).toLocaleString('ko-KR') : ''}
              onChange={(e) => {
                const value = e.target.value.replace(/,/g, '')
                if (value === '' || /^\d+$/.test(value)) {
                  setPrice(value)
                }
              }}
              placeholder="0"
              className={`w-full px-4 py-3 rounded-lg border ${
                theme === 'light'
                  ? 'bg-white border-gray-300 text-gray-900'
                  : 'bg-gray-800 border-gray-700 text-white'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
              required
            />
            <span className={`absolute right-4 top-1/2 -translate-y-1/2 ${
              theme === 'light' ? 'text-gray-500' : 'text-gray-400'
            }`}>
              원
            </span>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Brand */}
          <div>
            <label className={`block text-sm font-semibold mb-2 ${
              theme === 'light' ? 'text-gray-700' : 'text-gray-300'
            }`}>
              브랜드
            </label>
            <input
              type="text"
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
              placeholder="예: 마레스, 크레시, 스펙트라 등"
              className={`w-full px-4 py-3 rounded-lg border ${
                theme === 'light'
                  ? 'bg-white border-gray-300 text-gray-900'
                  : 'bg-gray-800 border-gray-700 text-white'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
          </div>

          {/* Model */}
          <div>
            <label className={`block text-sm font-semibold mb-2 ${
              theme === 'light' ? 'text-gray-700' : 'text-gray-300'
            }`}>
              모델명
            </label>
            <input
              type="text"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              placeholder="모델명"
              className={`w-full px-4 py-3 rounded-lg border ${
                theme === 'light'
                  ? 'bg-white border-gray-300 text-gray-900'
                  : 'bg-gray-800 border-gray-700 text-white'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
          </div>

          {/* Purchase Date */}
          <div>
            <label className={`block text-sm font-semibold mb-2 ${
              theme === 'light' ? 'text-gray-700' : 'text-gray-300'
            }`}>
              구매 시기
            </label>
            <input
              type="text"
              value={purchaseDate}
              onChange={(e) => setPurchaseDate(e.target.value)}
              placeholder="예: 2023년 3월"
              className={`w-full px-4 py-3 rounded-lg border ${
                theme === 'light'
                  ? 'bg-white border-gray-300 text-gray-900'
                  : 'bg-gray-800 border-gray-700 text-white'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
          </div>

          {/* Usage Count */}
          <div>
            <label className={`block text-sm font-semibold mb-2 ${
              theme === 'light' ? 'text-gray-700' : 'text-gray-300'
            }`}>
              사용 횟수
            </label>
            <input
              type="text"
              value={usageCount}
              onChange={(e) => setUsageCount(e.target.value)}
              placeholder="예: 10회, 50로그 등"
              className={`w-full px-4 py-3 rounded-lg border ${
                theme === 'light'
                  ? 'bg-white border-gray-300 text-gray-900'
                  : 'bg-gray-800 border-gray-700 text-white'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
          </div>

          {/* Size */}
          <div>
            <label className={`block text-sm font-semibold mb-2 ${
              theme === 'light' ? 'text-gray-700' : 'text-gray-300'
            }`}>
              사이즈
            </label>
            <input
              type="text"
              value={size}
              onChange={(e) => setSize(e.target.value)}
              placeholder="예: M, L, 270mm 등"
              className={`w-full px-4 py-3 rounded-lg border ${
                theme === 'light'
                  ? 'bg-white border-gray-300 text-gray-900'
                  : 'bg-gray-800 border-gray-700 text-white'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
          </div>

          {/* Maintenance Date */}
          <div>
            <label className={`block text-sm font-semibold mb-2 ${
              theme === 'light' ? 'text-gray-700' : 'text-gray-300'
            }`}>
              마지막 오버홀
            </label>
            <input
              type="text"
              value={maintenanceDate}
              onChange={(e) => setMaintenanceDate(e.target.value)}
              placeholder="예: 2024년 1월"
              className={`w-full px-4 py-3 rounded-lg border ${
                theme === 'light'
                  ? 'bg-white border-gray-300 text-gray-900'
                  : 'bg-gray-800 border-gray-700 text-white'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
          </div>
        </div>

        {/* Condition */}
        <div className="mb-6">
          <label className={`block text-sm font-semibold mb-2 ${
            theme === 'light' ? 'text-gray-700' : 'text-gray-300'
          }`}>
            상품 상태
          </label>
          <div className="grid grid-cols-4 gap-2">
            {conditions.map((cond) => (
              <button
                key={cond}
                type="button"
                onClick={() => setCondition(cond)}
                className={`py-2 rounded-lg transition-all ${
                  condition === cond
                    ? 'bg-blue-500 text-white'
                    : theme === 'light'
                      ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                {cond}
              </button>
            ))}
          </div>
        </div>

        {/* Transaction Type */}
        <div className="mb-6">
          <label className={`block text-sm font-semibold mb-2 ${
            theme === 'light' ? 'text-gray-700' : 'text-gray-300'
          }`}>
            거래 방법
          </label>
          <div className="grid grid-cols-3 gap-2">
            {[
              { value: 'direct', label: '직거래만' },
              { value: 'delivery', label: '택배만' },
              { value: 'both', label: '직거래/택배 모두' }
            ].map((type) => (
              <button
                key={type.value}
                type="button"
                onClick={() => setTransactionType(type.value)}
                className={`py-2 rounded-lg transition-all ${
                  transactionType === type.value
                    ? 'bg-blue-500 text-white'
                    : theme === 'light'
                      ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                {type.label}
              </button>
            ))}
          </div>
        </div>

        {/* Region */}
        <div className="mb-8">
          <label className={`block text-sm font-semibold mb-2 ${
            theme === 'light' ? 'text-gray-700' : 'text-gray-300'
          }`}>
            거래 지역
          </label>
          <select
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            className={`w-full px-4 py-3 rounded-lg border ${
              theme === 'light'
                ? 'bg-white border-gray-300 text-gray-900'
                : 'bg-gray-800 border-gray-700 text-white'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <option value="">지역 선택 (선택사항)</option>
            {regions.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white py-3 rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all font-bold text-base disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? '등록 중...' : '상품 등록하기'}
        </button>
      </form>
    </div>
  )
}
